import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useMeg, useVirksomheter } from '../api/sporringer.js';

const LAGERNOKKEL = 'es.valgt-virksomhet';

/** Hva skjermene ser på: én virksomhet, eller et helt kommunalområde. */
export interface Omfang {
  readonly type: 'virksomhet' | 'omrade';
  readonly id: string;
}

interface Verdi {
  readonly omfang: Omfang | undefined;
  /** Snarvei for de fire modulene som bare gir mening for én virksomhet. */
  readonly virksomhetId: string | undefined;
  readonly settOmfang: (omfang: Omfang) => void;
}

const Kontekst = createContext<Verdi>({
  omfang: undefined,
  virksomhetId: undefined,
  settOmfang: () => undefined,
});

/**
 * Leser lagret valg, også fra forrige versjon.
 *
 * Før dette lagret vi en bar virksomhets-id. Den skal leses som en virksomhet,
 * ikke forkastes som ugyldig — ellers mister alle valget sitt ved oppgraderingen
 * uten å skjønne hvorfor.
 */
function lesLagret(): Omfang | undefined {
  try {
    const rå = localStorage.getItem(LAGERNOKKEL);
    if (rå === null) return undefined;
    if (!rå.startsWith('{')) return { type: 'virksomhet', id: rå };
    const tolket: unknown = JSON.parse(rå);
    if (
      typeof tolket === 'object' &&
      tolket !== null &&
      'type' in tolket &&
      'id' in tolket &&
      (tolket.type === 'virksomhet' || tolket.type === 'omrade') &&
      typeof tolket.id === 'string'
    ) {
      return { type: tolket.type, id: tolket.id };
    }
    return undefined;
  } catch {
    // Privat vindu, eller en nettleser som nekter. Ikke verdt å feile på.
    return undefined;
  }
}

/**
 * Hva man ser på, ett sted.
 *
 * Valget lagres i nettleseren, ikke i databasen: hvilken virksomhet eller
 * hvilket område en person så på sist er en preferanse ved denne maskinen, ikke
 * data om kommunen.
 */
export function Virksomhetsvelger({ children }: { children: ReactNode }): ReactElement {
  const virksomheter = useVirksomheter();
  const meg = useMeg();
  const [valgt, settValgt] = useState<Omfang | undefined>(lesLagret);

  const liste = virksomheter.data;

  // UTLEDET, ikke satt i en effekt. Er det lagrede borte — virksomheten
  // deaktivert, tilgangen fjernet, området tomt — faller vi tilbake til den
  // første virksomheten. Gjort med setState i en effekt ville hver
  // liste-endring gitt en ekstra render, og skjermene ville rukket å spørre
  // etter noe brukeren ikke lenger har.
  const finnes =
    valgt === undefined
      ? false
      : valgt.type === 'virksomhet'
        ? liste?.some((v) => v.id === valgt.id) === true
        : // For et område er spørsmålet om det er TILDELT, ikke om man tilfeldigvis
          // har en virksomhet i det. Sjekket man det siste, ville et lagret
          // områdevalg overlevd at områdetilgangen ble fjernet — og skjermene
          // ville spurt etter et tall API-et nå svarer 403 på.
          meg.data?.omradetilganger.some((o) => o.kommunalomradeId === valgt.id) === true;

  const effektivt: Omfang | undefined = finnes
    ? valgt
    : liste?.[0] === undefined
      ? undefined
      : { type: 'virksomhet', id: liste[0].id };

  const settOmfang = useCallback((omfang: Omfang) => {
    settValgt(omfang);
    try {
      localStorage.setItem(LAGERNOKKEL, JSON.stringify(omfang));
    } catch {
      // Valget gjelder fortsatt for denne økten; det overlever bare ikke.
    }
  }, []);

  const verdi = useMemo(
    () => ({
      omfang: effektivt,
      virksomhetId: effektivt?.type === 'virksomhet' ? effektivt.id : undefined,
      settOmfang,
    }),
    [effektivt, settOmfang],
  );
  return <Kontekst.Provider value={verdi}>{children}</Kontekst.Provider>;
}

export function useValgtVirksomhet(): Verdi {
  return useContext(Kontekst);
}
